/**
 * InvoiceListReport (LISTS-01, on the universal engine). ESC's Invoice List,
 * rebuilt on the SAME list engine as Customers: choose any column, filter by any
 * field, group (e.g. by Dept or Salesperson) with Σ subtotals + a grand total,
 * save views, export CSV. Read-only AR per DEC-22 — drilling opens the invoice;
 * the customer name Qualifies to the 360. Click a row to peek; "Open invoice"
 * is the deliberate drill.
 *
 * Competitor frame: ServiceTitan's invoice list + QuickBooks' transaction
 * reports — filter to a set, subtotal by a dimension, act on the set. The dollar
 * grand-total + per-group subtotals are where this engine earns its keep.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Receipt, X, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { CustomerLink } from "@/components/links";
import { pagedAll } from "@/lib/pagedAll";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import {
  CsvButton, DateRangeControl, ViewsMenu, ListSearch, downloadCsv, pillSelectCls, isoDaysAgo, isoToday,
  useSort, useColumns, ColumnsButton, ListTable, FiltersButton, FilterChips, applyFilters,
  type ListField, type FilterCond,
} from "./listCore";

interface Row {
  invoice: string;
  inv_date: string | null;
  due_date: string | null;
  custno: string | null;
  locno: string | null;
  ship_name: string | null;
  ship_csz: string | null;
  dept: string | null;
  inv_type: string | null;
  job_number: string | null;
  po_num: string | null;
  agrmt_no: string | null;
  salesman: string | null;
  sl_terms: string | null;
  inv_amount: number | null;
  labor_cost: number | null;
  mat_cost: number | null;
  dispatch: string | null;
}

interface Filters { from: string; to: string; q: string; dept: string }
const DEFAULT_FILTERS = (): Filters => ({ from: isoDaysAgo(30), to: isoToday(), q: "", dept: "" });
const COLS = "invoice,inv_date,due_date,custno,locno,ship_name,ship_csz,dept,inv_type,job_number,po_num,agrmt_no,salesman,sl_terms,inv_amount,labor_cost,mat_cost,dispatch";
const DISPLAY_CAP = 1500;

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (d: string | null) => {
  if (!d) return "—";
  try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; }
};

export function InvoiceListReport() {
  const navigate = useNavigateWithBack();
  const [f, setF] = useState<Filters>(DEFAULT_FILTERS);
  const [conds, setConds] = useState<FilterCond[]>([]);
  const [selected, setSelected] = useState<Row | null>(null);
  const sort = useSort<Row>("inv_date", "desc");

  // The Invoice field catalog — the office picks columns, groups by Dept /
  // Salesperson / Type, and totals the money columns. Same shape Customers uses.
  const fields: ListField<Row>[] = useMemo(() => [
    { id: "inv_date", type: "date", filterable: true, label: "Date", value: (r) => r.inv_date, render: (r) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(r.inv_date)}</span> },
    { id: "invoice", type: "text", filterable: true, label: "Invoice #", value: (r) => r.invoice, render: (r) => <span className="font-mono text-xs text-slate-700">{r.invoice}</span> },
    { id: "customer", type: "text", filterable: true, col: "ship_name", label: "Customer", value: (r) => r.ship_name ?? r.custno, render: (r) => r.custno
        ? <CustomerLink custno={r.custno} locno={r.locno ?? undefined} className="text-sm">{r.ship_name ?? r.custno}</CustomerLink>
        : <span className="text-sm text-slate-700">{r.ship_name ?? "—"}</span> },
    { id: "dept", type: "text", filterable: true, groupable: true, label: "Dept", value: (r) => r.dept },
    { id: "inv_type", type: "text", filterable: true, groupable: true, label: "Type", value: (r) => r.inv_type, defaultVisible: false },
    { id: "inv_amount", numeric: true, totalable: true, type: "number", filterable: true, label: "Amount", value: (r) => Number(r.inv_amount) || 0, render: (r) => r.inv_amount != null ? <span className="font-medium tabular-nums text-slate-800">{money(Number(r.inv_amount))}</span> : "—" },
    { id: "labor_cost", numeric: true, totalable: true, type: "number", filterable: true, label: "Labor", value: (r) => Number(r.labor_cost) || 0, render: (r) => r.labor_cost != null ? money(Number(r.labor_cost)) : "—", defaultVisible: false },
    { id: "mat_cost", numeric: true, totalable: true, type: "number", filterable: true, label: "Materials", value: (r) => Number(r.mat_cost) || 0, render: (r) => r.mat_cost != null ? money(Number(r.mat_cost)) : "—", defaultVisible: false },
    { id: "salesman", type: "text", filterable: true, groupable: true, label: "Salesperson", value: (r) => r.salesman, defaultVisible: false },
    { id: "job_number", type: "text", filterable: true, label: "Job #", value: (r) => r.job_number, defaultVisible: false },
    { id: "po_num", type: "text", filterable: true, label: "PO #", value: (r) => r.po_num, defaultVisible: false },
    { id: "dispatch", type: "text", filterable: true, label: "Dispatch #", value: (r) => r.dispatch, defaultVisible: false },
    { id: "agrmt_no", type: "text", filterable: true, label: "Agreement #", value: (r) => r.agrmt_no, defaultVisible: false },
    { id: "due_date", type: "date", filterable: true, label: "Due Date", value: (r) => r.due_date, render: (r) => fmtDate(r.due_date), defaultVisible: false },
    { id: "ship_csz", type: "text", filterable: true, label: "City / State / Zip", value: (r) => r.ship_csz, defaultVisible: false },
    { id: "sl_terms", type: "text", filterable: true, label: "Terms", value: (r) => r.sl_terms, defaultVisible: false },
  ], []);
  const cols = useColumns("invoices", fields);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["list_invoices", f.from, f.to, conds],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Row[]> => pagedAll<Row>(() => {
      let q = supabase.from("mirror_sales").select(COLS).gte("inv_date", f.from).lte("inv_date", f.to);
      q = applyFilters(q, conds, fields as ListField<unknown>[]);
      return q.order("inv_date", { ascending: false }).order("invoice", { ascending: false });
    }),
  });
  const all = data ?? [];

  // The toolbar quick-filters (dept + free text) narrow client-side; the
  // any-field Filters narrow server-side. Total + count reflect the full set.
  const filtered = useMemo(() => {
    const ql = f.q.trim().toLowerCase();
    return all.filter((r) => {
      if (f.dept && (r.dept ?? "") !== f.dept) return false;
      if (ql) {
        const hay = [r.invoice, r.ship_name, r.custno, r.po_num, r.job_number, r.dispatch].join(" ").toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [all, f.q, f.dept]);

  const grandTotal = useMemo(() => filtered.reduce((s, r) => s + (Number(r.inv_amount) || 0), 0), [filtered]);
  const deptOpts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of all) { if (r.dept) m.set(r.dept, (m.get(r.dept) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [all]);

  const shownRows = filtered.slice(0, DISPLAY_CAP);
  const setPart = (p: Partial<Filters>) => setF((prev) => ({ ...prev, ...p }));

  function exportCsv() {
    const shown = cols.visible.map((id) => fields.find((x) => x.id === id)).filter(Boolean) as ListField<Row>[];
    downloadCsv(
      `invoices_${f.from}_${f.to}.csv`,
      shown.map((c) => c.label),
      filtered.map((r) => shown.map((c) => { const v = c.value(r); return v == null ? "" : String(v); })),
    );
  }

  return (
    <div className="min-w-0 flex-1">
      {/* TOOLBAR-REFRESH — a search-forward command row + a clean scoping row */}
      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch value={f.q} onChange={(v) => setPart({ q: v })} placeholder="Search invoice #, customer, PO, job, dispatch…" />
          <FiltersButton fields={fields} conds={conds} onChange={setConds} />
          <ColumnsButton fields={fields} cols={cols} />
          <CsvButton onClick={exportCsv} />
          <ViewsMenu entity="invoices" currentFilters={f as unknown as Record<string, unknown>} onApply={(saved) => setF({ ...DEFAULT_FILTERS(), ...(saved as Partial<Filters>) })} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl from={f.from} to={f.to} onChange={(from, to) => setF((prev) => ({ ...prev, from, to }))} />
          <select value={f.dept} onChange={(e) => setPart({ dept: e.target.value })} className={pillSelectCls} title="Filter by dept">
            <option value="">All depts</option>
            {deptOpts.map(([d, n]) => <option key={d} value={d}>{d} ({n})</option>)}
          </select>
          {conds.length > 0 && <FilterChips conds={conds} fields={fields} onChange={setConds} />}
          <div className="ml-auto whitespace-nowrap text-sm tabular-nums text-slate-500">
            <strong className="text-slate-800">{filtered.length.toLocaleString()}</strong> invoices · <strong className="text-emerald-700">{money(grandTotal)}</strong>{isFetching ? " · …" : ""}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : isError ? (
        <Card className="p-6"><p className="text-sm text-red-600">{(error as Error)?.message}</p></Card>
      ) : (
        <div className="flex items-start gap-3">
          <Card className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Receipt className="h-3.5 w-3.5" /></span>
              <span className="text-[14.5px] font-bold tracking-tight text-slate-900">Invoice List</span>
              <InfoTip label="How this list works">Read-only: accounting stays in ESC. Click a row to see the invoice. For subtotals, group by Dept or Salesperson and total Amount in the Fields menu.</InfoTip>
              <span className="ml-auto text-xs tabular-nums text-slate-500">{filtered.length.toLocaleString()} invoices{filtered.length > DISPLAY_CAP ? ` · showing first ${DISPLAY_CAP.toLocaleString()}` : ""}</span>
            </div>
            <div className="max-h-[68vh] overflow-auto">
              <ListTable
                rows={shownRows}
                fields={fields}
                cols={cols}
                sort={sort}
                getKey={(r) => r.invoice}
                onRowClick={(r) => setSelected(r)}
                selectedKey={selected?.invoice ?? null}
                emptyText="No invoices match — widen the dates or clear a filter."
              />
            </div>
            {filtered.length > DISPLAY_CAP && (
              <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
                The header dollar total covers all {filtered.length.toLocaleString()} matches; the table shows the first {DISPLAY_CAP.toLocaleString()} — narrow the dates for exact on-screen subtotals.
              </p>
            )}
          </Card>
          {selected && (
            <InvoiceDetailsPanel
              row={selected}
              onClose={() => setSelected(null)}
              onOpen={() => navigate(`/invoice/${selected.invoice}`)}
              onQualify={selected.custno ? () => navigate(`/customers/${selected.custno}`) : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

// The invoice peek panel (two-window model: row click peeks here; "Open invoice"
// is the deliberate drill). Mirrors the Customers details panel shape.
function InvoiceDetailsPanel({ row, onClose, onOpen, onQualify }: { row: Row; onClose: () => void; onOpen: () => void; onQualify?: () => void }) {
  const meta: [string, string | null][] = [
    ["Dept", row.dept],
    ["Type", row.inv_type],
    ["Salesperson", row.salesman],
    ["Job #", row.job_number],
    ["PO #", row.po_num],
    ["Dispatch #", row.dispatch],
    ["Agreement #", row.agrmt_no],
    ["Terms", row.sl_terms],
    ["Due", row.due_date ? fmtDate(row.due_date) : null],
  ];
  return (
    <div className="w-72 shrink-0">
      <Card className="overflow-hidden">
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="min-w-0">
            <div className="font-mono text-sm font-bold text-slate-900">#{row.invoice}</div>
            <div className="text-[11px] text-slate-400">{fmtDate(row.inv_date)}</div>
          </div>
          <button onClick={onClose} title="Close" className="shrink-0 text-slate-300 transition-colors hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <div className="text-[11.5px] font-semibold text-slate-500">Amount</div>
            <div className="text-2xl font-bold tabular-nums text-slate-900">{row.inv_amount != null ? money(Number(row.inv_amount)) : "—"}</div>
          </div>
          <div className="text-sm leading-snug text-slate-700">
            <div className="font-medium">{row.ship_name ?? row.custno ?? "—"}</div>
            {row.ship_csz && <div className="text-slate-500">{row.ship_csz}</div>}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-slate-100 pt-3">
            {meta.filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="min-w-0">
                <div className="text-[11.5px] font-semibold text-slate-500">{k}</div>
                <div className="truncate text-xs text-slate-700">{v}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 pt-1">
            <button onClick={onOpen} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700">
              Open invoice <ArrowRight className="h-3.5 w-3.5" />
            </button>
            {onQualify && (
              <button onClick={onQualify} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">
                Qualify customer
              </button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
